import { Component, Input, Optional, Self } from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { toLocalDateString } from '../../date.util';

/**
 * A Material-styled date field that offers BOTH input modes at once:
 *   1. digital entry — the segmented MM/DD/YYYY fields of a native type="date"
 *   2. calendar selection — a calendar button that opens the native date picker
 * The native calendar indicator is hidden (the Material form field clips it), and
 * a matSuffix button opens the picker via showPicker() instead. Model value stays
 * a 'YYYY-MM-DD' string, so it's a drop-in for a plain <input type="date">.
 * For forms whose model is a Date object (the old matDatepicker bindings), set
 * emitAs="date": writeValue then accepts a Date and onChange emits a Date at
 * local midnight, so those bindings convert with no TS changes.
 *
 *   <app-date-field label="Effective From" [(ngModel)]="from"></app-date-field>
 *   <app-date-field label="Due" formControlName="dueOn" errorText="Due date is required"></app-date-field>
 *   <app-date-field label="Date" emitAs="date" [(ngModel)]="selectedDate"></app-date-field>
 *
 * mode="datetime" switches the native control to type="datetime-local" and the
 * model to 'YYYY-MM-DDTHH:mm'. Same two ways in: type the segments, or press
 * the button. Not available with emitAs="date" - a Date carrying a time is a
 * different contract from the local-midnight Date the date mode emits, and no
 * caller has asked for it.
 */
@Component({
  selector: 'app-date-field',
  templateUrl: './date-field.component.html',
  styleUrls: ['./date-field.component.css'],
})
export class DateFieldComponent implements ControlValueAccessor {
  @Input() label = '';
  @Input() width = '100%';
  @Input() disabled = false;
  /** 'string' (default) keeps a 'YYYY-MM-DD' model; 'date' reads/writes Date objects. */
  @Input() emitAs: 'string' | 'date' = 'string';
  /** 'date' (default) is a day; 'datetime' adds a time of day. */
  @Input() mode: 'date' | 'datetime' = 'date';
  /** Shown under the field when the bound control is invalid and touched. */
  @Input() errorText = 'Required';
  @Input() set min(v: string | Date | null) { this.minStr = this.toStr(v); }
  @Input() set max(v: string | Date | null) { this.maxStr = this.toStr(v); }

  minStr: string | null = null;
  maxStr: string | null = null;
  value: string | null = null;

  private onChangeFn: (v: string | Date | null) => void = () => {};
  onTouched: () => void = () => {};

  // Registering as the value accessor here (instead of via NG_VALUE_ACCESSOR)
  // gives us the NgControl for error-state display without a circular DI.
  constructor(@Optional() @Self() public ngControl: NgControl | null) {
    if (this.ngControl) { this.ngControl.valueAccessor = this; }
  }

  get showError(): boolean {
    const c = this.ngControl;
    return !!c && !!c.invalid && (!!c.touched || !!c.dirty);
  }

  writeValue(v: string | Date | null): void { this.value = this.toStr(v); }
  registerOnChange(fn: (v: string | Date | null) => void): void { this.onChangeFn = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }
  setDisabledState(isDisabled: boolean): void { this.disabled = isDisabled; }

  onInput(e: Event): void {
    this.value = (e.target as HTMLInputElement).value || null;
    if (this.emitAs === 'date') {
      const m = this.value ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(this.value) : null;
      this.onChangeFn(m ? new Date(+m[1], +m[2] - 1, +m[3]) : null);
    } else {
      this.onChangeFn(this.value);
    }
  }

  // Open the native calendar. showPicker() is supported by modern browsers; if not,
  // focusing the input still lets the user open it or type.
  openPicker(el: HTMLInputElement): void {
    if (this.disabled) return;
    const anyEl = el as any;
    if (typeof anyEl.showPicker === 'function') {
      try { anyEl.showPicker(); return; } catch { /* fall through to focus */ }
    }
    el.focus();
  }

  private toStr(v: string | Date | null): string | null {
    if (v instanceof Date && !isNaN(v.getTime())) {
      return toLocalDateString(v);
    }
    if (typeof v !== 'string' || !v) return null;
    // A datetime keeps its time; a date is cut back to the day. Slicing to 16
    // rather than the whole string drops any seconds and zone the server sent,
    // which a datetime-local input will not accept.
    return this.mode === 'datetime' ? v.slice(0, 16) : v.slice(0, 10);
  }
}
